




import { Vue, Component, Watch } from "vue-property-decorator";
import { Getter } from "vuex-class";
import api from "@/services/api.service";

/**
 * Heartbeat component responsible for maintaining the users session with their **local** data service. This does
 * not handle heartbeat'ing to remote regional servers.
 */
@Component
export default class Heartbeat extends Vue {
	@Getter private isLoggedIn: boolean;

	private localHeartbeatInterval: NodeJS.Timeout = null;

	// Heartbeat twice every minute
	private readonly second = 1000;
	private readonly heartbeatIntervalMs = 30 * this.second;

	/**
	 * Returns true if the current route requires us to skip performing heartbeats.
	 */
	private get disableHeartbeat() {
		// If the current route has no metadata, or has no specific 'heartbeat' value,
		// then assume we can continue heartbeat-ing.
		if (!this.$route.meta || !this.$route.meta.hasOwnProperty("heartbeat")) {
			return false;
		} else {
			return !this.$route.meta.heartbeat;
		}
	}

	/**
	 * Returns true if the component can heartbeat (user is logged in and
	 * they're on a page that does not explicitly disable heartbeat-ing).
	 */
	private get canHeartbeat() {
		return this.isLoggedIn && !this.disableHeartbeat;
	}

	/**
	 * Starts heartbeating to the local data service, refreshing the users
	 * session every 5 seconds. If the session expires or an error is thrown,
	 * the user is notified and the heartbeat stops.
	 */
	private startLocalHeartbeat() {
		this.localHeartbeatInterval = setInterval(async () => {
			if (!this.canHeartbeat) {
				return;
			}

			try {
				const { status } = await api.heartbeatToLocalServer();

				if (status != 200) {
					throw new Error();
				}
			} catch (err) {
				this.$notify({
					title: "Session Expired",
					text: "Your local session has expired",
					type: "error"
				});

				this.stopLocalHeartbeat();
			}
		}, this.heartbeatIntervalMs);
	}

	/**
	 * Stops heartbeating to the local data service.
	 */
	private stopLocalHeartbeat() {
		clearInterval(this.localHeartbeatInterval);
		this.localHeartbeatInterval = null;
	}

	/**
	 * Watches for when the user logs in. If the local data service isn't currently
	 * being hit with a heartbeat, set one up.
	 */
	@Watch("canHeartbeat", { immediate: true })
	private onCanHeartbeatChanged(value: boolean) {
		if (value) {
			if (this.localHeartbeatInterval) {
				return;
			}

			this.startLocalHeartbeat();
		} else {
			this.stopLocalHeartbeat();
			return;
		}
	}
}
